import { useState, useEffect, useCallback, useMemo, useRef } from "react";
import { useNavigate, useParams } from "react-router-dom";
import axios from "axios";
import "@/App.css";
import { Flask, Plus, Trash, Printer, ArrowClockwise, MagnifyingGlass, Shield, CheckCircle, XCircle } from "@phosphor-icons/react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { toast } from "@/components/ui/sonner";
import { useAuth } from "@/contexts/AuthContext";
import { NavTabs } from "@/components/NavTabs";
import { SapConnectionStatus } from "@/components/SapConnectionStatus";
import { ErpConnectionStatus } from "@/components/ErpConnectionStatus";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { RequestPrintSlip } from "@/components/RequestPrintSlip";
import { NpdConsumptionTab } from "@/components/NpdConsumptionTab";
import { NpdActivityReportTab } from "@/components/NpdActivityReportTab";
import { NpdProjectSpendTab } from "@/components/NpdProjectSpendTab";
import { NpdReturnToStoreTab } from "@/components/NpdReturnToStoreTab";
import { NpdIssueForProductionTab } from "@/components/NpdIssueForProductionTab";
import { NpdProjectPicker } from "@/components/NpdProjectPicker";
import { NpdToolingApprovalTab } from "@/components/NpdToolingApprovalTab";
import { projectCustomerName } from "@/lib/projectCustomer";
import { NpdTaskPicker } from "@/components/NpdTaskPicker";
import { NpdNoteField } from "@/components/NpdNoteField";
import { RoundedUpFlag } from "@/components/RoundedUpFlag";
import { formatUnit } from "../lib/formatUnit";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const formatQty = (v) => (v == null ? "\u2014" : Number(v).toLocaleString("en-IN", { maximumFractionDigits: 4 }));

const STATUS_BADGE = {
  pending: { label: "Awaiting Store", tone: "bg-[#FFFAEB] text-[#B54708] border-[#FEDF89]" },
  issuing: { label: "Issuing Stock...", tone: "bg-[#EFF8FF] text-[#0E7C86] border-[#B2DDFF]" },
  awaiting_receipt: { label: "Confirm Your Receipt", tone: "bg-[#FEF6EE] text-[#B93815] border-[#F9DBAF]" },
  partial_pending_planner: { label: "Awaiting Your Approval", tone: "bg-[#EFF8FF] text-[#175CD3] border-[#B2DDFF]" },
  resolved_balance_pending: { label: "SAP Rejected - Reopened", tone: "bg-[#FEF6EE] text-[#B93815] border-[#F9DBAF]" },
  resolved: { label: "Issued", tone: "bg-[#ECFDF3] text-[#027A48] border-[#ABEFC6]" },
  cancelled: { label: "Cancelled", tone: "bg-[#FEF3F2] text-[#B42318] border-[#FECDCA]" },
};

const PURPOSE_LABEL = { consumption: "Production", production: "NPD Project Consumption" };

const emptyLine = () => ({ key: Math.random().toString(36).slice(2), product_id: "", description: "", uom: "", quantity: "" });

const ItemRow = ({ line, index, siteId, onChange, onRemove, canRemove }) => {
  const [results, setResults] = useState([]);
  const [searching, setSearching] = useState(false);
  const [open, setOpen] = useState(false);
  const [stock, setStock] = useState(null);
  const [refreshing, setRefreshing] = useState(false);
  const timer = useRef(null);

  const search = useCallback((q) => {
    clearTimeout(timer.current);
    if (!q || q.trim().length < 2) {
      setResults([]);
      return;
    }
    timer.current = setTimeout(async () => {
      setSearching(true);
      try {
        const { data } = await axios.get(`${API}/npd-store-requests/material-search`, { params: { q: q.trim() } });
        setResults(data.materials || []);
        setOpen(true);
      } catch {
        setResults([]);
      } finally {
        setSearching(false);
      }
    }, 400);
  }, []);

  const pick = async (m) => {
    onChange({ ...line, product_id: m.product_id, description: m.description || "", uom: m.uom || "" });
    setOpen(false);
    setResults([]);
    if (!siteId) return;
    try {
      const { data } = await axios.get(`${API}/npd-store-requests/item-stock`, { params: { site_id: siteId, product_id: m.product_id } });
      setStock(data);
    } catch {
      setStock(null);
    }
  };

  const refreshFromSap = async () => {
    if (!line.product_id || !siteId) return;
    setRefreshing(true);
    try {
      const { data } = await axios.post(`${API}/npd-store-requests/refresh-item-stock`, null,
        { params: { site_id: siteId, product_id: line.product_id } });
      setStock(data);
      const total = (data.locations || []).reduce((sum, l) => sum + (l.qty || 0), 0);
      toast.success(`${line.product_id}: ${formatQty(total)} usable in ${siteId} as of now`);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "SAP could not be read just now - try again in a moment.");
    } finally {
      setRefreshing(false);
    }
  };

  return (
    <tr className={index % 2 === 0 ? "bg-white" : "bg-[#F9FAFB]"} data-testid={`npd-item-row-${index}`}>
      <td className="border border-[#D0D5DD] px-2 py-1.5 align-top">
        <Input
          value={line.product_id}
          placeholder="Type item code..."
          onChange={(e) => { onChange({ ...line, product_id: e.target.value.toUpperCase() }); search(e.target.value); }}
          className="h-8 font-mono text-[12px]"
          data-testid={`npd-item-code-input-${index}`}
        />
        {open && results.length > 0 && (
          <div className="mt-1 max-h-56 overflow-auto bg-white border border-[#D0D5DD] rounded-sm shadow-sm" data-testid={`npd-item-search-results-${index}`}>
            {results.map((m) => (
              <button
                key={m.product_id}
                type="button"
                onClick={() => pick(m)}
                className="w-full text-left px-2 py-1.5 text-[12px] hover:bg-[#F0FDF9] border-b border-[#F2F4F7] last:border-b-0"
                data-testid={`npd-item-search-option-${index}-${m.product_id}`}
              >
                <span className="font-mono font-bold text-[#175CD3]">{m.product_id}</span>
                <span className="text-[#667085]"> &middot; {m.description || "No description"} &middot; {formatUnit(m.uom)}</span>
              </button>
            ))}
          </div>
        )}
        {searching && <p className="text-[10px] text-[#667085] mt-0.5">Searching...</p>}
      </td>
      <td className="border border-[#D0D5DD] px-2 py-1.5 align-top text-[12px] text-[#475467]" data-testid={`npd-item-description-${index}`}>
        {line.description || "\u2014"}
        {line.product_id && siteId && (
          <button
            type="button"
            onClick={refreshFromSap}
            disabled={refreshing}
            className="ml-2 text-[10px] font-bold text-[#0E7C86] hover:underline disabled:opacity-40"
            data-testid={`npd-item-stock-refresh-${index}`}
          >
            {refreshing ? "reading SAP..." : "refresh from SAP"}
          </button>
        )}
        {stock && (
          <>
            <p className="text-[10px] text-[#027A48] mt-0.5" data-testid={`npd-item-stock-${index}`}>
              {(stock.locations || []).length === 0
                ? "No usable stock at this site"
                : stock.locations.map((l) => `${l.warehouse_id?.split("/").pop()}: ${formatQty(l.qty)}`).join(" \u00b7 ")}
              {stock.as_of && (
                <span className="text-[#667085]">
                  {" "}&middot; {stock.live ? "read from SAP just now" : `as of ${new Date(stock.as_of).toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" })}`}
                </span>
              )}
            </p>
            {(stock.blocked || []).length > 0 && (
              <p className="text-[10px] text-[#B54708] mt-0.5" data-testid={`npd-item-stock-blocked-${index}`}>
                SAP also holds {stock.blocked.map((l) => `${formatQty(l.qty)} in ${l.warehouse_id?.split("/").pop()} (${l.blocked_reason})`).join(" \u00b7 ")} - the store cannot issue that
              </p>
            )}
          </>
        )}
      </td>
      <td className="border border-[#D0D5DD] px-2 py-1.5 align-top text-[12px]" data-testid={`npd-item-uom-${index}`}>{line.uom ? formatUnit(line.uom) : "\u2014"}</td>
      <td className="border border-[#D0D5DD] px-2 py-1.5 align-top">
        <Input
          type="number"
          min="0"
          step="any"
          value={line.quantity}
          placeholder="Qty"
          onChange={(e) => onChange({ ...line, quantity: e.target.value })}
          className="h-8 w-28 text-right tabular-nums"
          data-testid={`npd-item-qty-input-${index}`}
        />
      </td>
      <td className="border border-[#D0D5DD] px-2 py-1.5 align-top">
        <Button
          type="button"
          variant="outline"
          size="sm"
          className="h-8 bg-white text-[#B42318]"
          disabled={!canRemove}
          onClick={onRemove}
          data-testid={`npd-item-remove-${index}`}
        >
          <Trash size={13} />
        </Button>
      </td>
    </tr>
  );
};

export default function NpdStoreRequestPage() {
  const [sites, setSites] = useState([]);
  const [siteId, setSiteId] = useState("");
  const [purpose, setPurpose] = useState("production");
  const [warehouses, setWarehouses] = useState([]);
  const [targetWarehouse, setTargetWarehouse] = useState("");
  const [remark, setRemark] = useState("");
  const [projectId, setProjectId] = useState("");
  const [projectName, setProjectName] = useState("");
  const [taskId, setTaskId] = useState("");
  const [taskName, setTaskName] = useState("");
  const [newCustomer, setNewCustomer] = useState("");
  const [lines, setLines] = useState([emptyLine()]);
  const [submitting, setSubmitting] = useState(false);
  const [requests, setRequests] = useState([]);
  const [loadingRequests, setLoadingRequests] = useState(true);
  const [printTarget, setPrintTarget] = useState(null);
  const { user, hasPageAccess } = useAuth();
  // A tooling approver may hold neither the NPD request right nor any store
  // data access - their Store Requests section (and its loads) stay off.
  const canStore = hasPageAccess("npd_store_request");
  const canApprove = hasPageAccess("npd_tooling_head_approval") || hasPageAccess("npd_tooling_management_approval");
  const npdSections = [
    canStore && { key: "store", label: "Store Requests and Approvals" },
    canStore && { key: "tooling", label: "Tooling Budget Requests" },
    canApprove && { key: "approvals", label: "Tooling Budget Approvals" },
  ].filter(Boolean);
  // Jun 2026, user's ask: NPD is its own top-level menu, so the section lives
  // in the URL (/npd-requests/<section>) and the menu links straight to it.
  const { section: urlSection } = useParams();
  const navigate = useNavigate();
  const section = npdSections.some((s) => s.key === urlSection) ? urlSection : (npdSections[0]?.key || "store");
  const setSection = (key) => navigate(`/npd-requests/${key}`);
  const [receiptBusyId, setReceiptBusyId] = useState("");
  const [cancelTarget, setCancelTarget] = useState(null);
  const [cancelRemark, setCancelRemark] = useState("");

  useEffect(() => {
    if (!canStore) return;
    axios.get(`${API}/npd-store-requests/sites`).then(({ data }) => {
      setSites(data.sites || []);
      setSiteId((prev) => prev || (data.sites || [])[0] || "");
    }).catch(() => toast.error("Could not load the site list"));
  }, [canStore]);

  const loadRequests = useCallback(async () => {
    if (!canStore) return;
    setLoadingRequests(true);
    try {
      const { data } = await axios.get(`${API}/npd-store-requests`);
      setRequests(data.requests || []);
    } catch {
      toast.error("Could not load your NPD requests");
    } finally {
      setLoadingRequests(false);
    }
  }, [canStore]);

  useEffect(() => { loadRequests(); }, [loadRequests]);

  useEffect(() => {
    if (!siteId || !canStore) return;
    axios.get(`${API}/npd-store-requests/warehouses`, { params: { site_id: siteId } }).then(({ data }) => {
      setWarehouses(data.warehouses || []);
      setTargetWarehouse(data.default_warehouse_id || "");
    }).catch(() => setWarehouses([]));
  }, [siteId, canStore]);

  useEffect(() => {
    if (!printTarget) return;
    const t = setTimeout(() => window.print(), 50);
    return () => clearTimeout(t);
  }, [printTarget]);

  useEffect(() => {
    const clear = () => setPrintTarget(null);
    window.addEventListener("afterprint", clear);
    return () => window.removeEventListener("afterprint", clear);
  }, []);

  const filledLines = useMemo(
    () => lines.filter((l) => l.product_id.trim() && Number(l.quantity) > 0),
    [lines],
  );

  // Every field is mandatory (user's ask) - the button says why it is off.
  const missingField = useMemo(() => {
    if (!siteId) return "Select a site";
    if (!purpose) return "Select a purpose";
    if (!targetWarehouse) return "Select the NPD godown";
    if (!projectId) return "Pick the SAP project this is for";
    if (!taskId) return "Pick the task this material is for";
    if (!remark.trim()) return "Add a note saying why you need this material";
    if (filledLines.length === 0) return "Add at least one item with a quantity";
    return "";
  }, [siteId, purpose, targetWarehouse, projectId, taskId, remark, filledLines]);

  const submit = async () => {
    if (missingField) return toast.error(missingField);
    setSubmitting(true);
    try {
      const { data } = await axios.post(`${API}/npd-store-requests`, {
        site_id: siteId,
        purpose,
        remark,
        project_id: projectId,
        task_id: taskId,
        new_customer_name: newCustomer.trim(),
        target_warehouse: `${siteId}/${targetWarehouse}`,
        lines: filledLines.map((l) => ({ product_id: l.product_id.trim().toUpperCase(), quantity: Number(l.quantity) })),
      });
      toast.success(`Request ${data._id} sent to the store`, {
        description: data.notified?.sent
          ? `Mailed to ${data.notified.recipients.join(", ")} - the mail is in your Sent Items if you need to chase it.`
          : undefined,
      });
      // Nov 4 2026: the request is recorded either way, but the requester has
      // to know when nobody was told, so they can chase the store themselves.
      if (data.notified && !data.notified.sent) {
        toast.warning(
          data.notified.reason === "no_store_user"
            ? `Nobody with Store Goods Issue rights is bound to ${data.notified.site_id} yet - no mail was sent`
            : "The request is raised, but the notification mail could not be sent",
          { description: "Please tell the store directly, and ask the Admin to give the right to the store person for this site.", duration: 12000 },
        );
      }
      setLines([emptyLine()]);
      setRemark("");
      setNewCustomer("");
      setProjectId("");
      setTaskId("");
      setTaskName("");
      setProjectName("");
      loadRequests();
    } catch (e) {
      // The note check comes back as a structured detail with the exact
      // advice - show that instead of "[object Object]".
      const detail = e.response?.data?.detail;
      if (detail && typeof detail === "object" && detail.guidance) {
        toast.error(detail.message, { description: detail.guidance.join(" ") });
      } else {
        toast.error(detail || "Could not raise this request");
      }
    } finally {
      setSubmitting(false);
    }
  };

  // NPD two-way approval: the store has handed the material over but SAP has
  // not moved anything yet - this is the step that actually posts it.
  const confirmReceipt = async (r) => {
    setReceiptBusyId(r._id);
    try {
      const { data } = await axios.post(`${API}/npd-store-requests/${r._id}/receipt`, { decision: "accept" });
      toast.success(`Receipt confirmed for ${r._id} - posting the movement in SAP now`);
      for (let i = 0; i < 40 && data.job_id; i++) {
        await new Promise((res) => setTimeout(res, 2000));
        const { data: job } = await axios.get(`${API}/store-requests/issue-status/${data.job_id}`);
        if (job.status === "done") {
          toast.success(`${r._id}: stock moved into your NPD godown in SAP`);
          break;
        }
        if (job.status === "failed") {
          toast.error(job.error || "SAP refused the movement - the store can issue it again");
          break;
        }
      }
      loadRequests();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Could not confirm the receipt");
    } finally {
      setReceiptBusyId("");
    }
  };

  // Only the person who raised it may act - an admin browsing everyone
  // else's requests gets no dead buttons (the API refuses them too).
  const isMine = (r) => (r.requester_email && user?.email && r.requester_email.toLowerCase() === user.email.toLowerCase())
    || (r.requester || "").toLowerCase() === (user?.name || "").toLowerCase();

  const cancelWithRemark = async () => {
    if (cancelRemark.trim().length < 5) return toast.error("Say what did not match - the store needs that remark");
    setReceiptBusyId(cancelTarget._id);
    try {
      await axios.post(`${API}/npd-store-requests/${cancelTarget._id}/receipt`, {
        decision: "cancel", remark: cancelRemark.trim(),
      });
      toast.success(`${cancelTarget._id} cancelled - the store has been emailed, nothing moved in SAP`);
      setCancelTarget(null);
      setCancelRemark("");
      loadRequests();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Could not cancel this request");
    } finally {
      setReceiptBusyId("");
    }
  };

  return (
    <div className="min-h-screen bg-[#F2F4F7] text-[#1D2939]">
      {printTarget && <RequestPrintSlip request={printTarget} heading="NPD Store Request" />}
      <header className="print:hidden min-h-16 bg-[#0E7C86] shadow-[0_1px_3px_0_rgba(16,24,40,0.15)] flex items-center justify-between px-3 sm:px-5 shrink-0 z-10 gap-2 sm:gap-4">
        <div className="flex items-center gap-3 shrink-0" data-testid="app-title">
          <div className="w-8 h-8 rounded-lg bg-white/15 flex items-center justify-center shrink-0">
            <Shield size={18} weight="fill" className="text-white" />
          </div>
          <div className="flex flex-col leading-tight">
            <span className="font-heading text-[16px] font-bold text-white tracking-tight">Production Control</span>
            <span className="font-sans text-[12px] text-white/70 hidden sm:inline">NPD Requests</span>
          </div>
        </div>
        <div className="w-px h-7 bg-white/25 shrink-0" />
        <div className="flex items-center gap-3 flex-1 justify-start min-w-0">
          <NavTabs />
        </div>
        <div className="hidden xl:flex items-center gap-2 shrink-0">
          <SapConnectionStatus />
          <ErpConnectionStatus />
        </div>
      </header>

      <main className="print:hidden max-w-[1400px] mx-auto p-4 sm:p-6 space-y-5">
        {/* Jun 2026, user's ask: "put all NPD menu action items in one place" -
            Store Requests and Approvals / Tooling Budget Requests (NPD raises)
            / Tooling Budget Approvals (head + management decide). Each item
            only appears for the right that actually opens it. */}
        <div className="flex items-center gap-2 flex-wrap" data-testid="npd-section-switch">
          {npdSections.map((s, i) => (
            <button
              key={s.key}
              type="button"
              onClick={() => setSection(s.key)}
              className={`px-4 py-2 rounded-sm text-sm font-bold border transition-colors ${section === s.key ? "bg-[#0E7C86] text-white border-[#0E7C86]" : "bg-white text-[#344054] border-[#D0D5DD] hover:border-[#0E7C86]"}`}
              data-testid={`npd-section-${s.key}`}
            >
              {npdSections.length > 1 ? `${i + 1}. ` : ""}{s.label}
            </button>
          ))}
        </div>

        {section === "tooling" && <NpdToolingApprovalTab mode="requests" />}
        {section === "approvals" && <NpdToolingApprovalTab mode="approvals" />}

        {section === "store" && canStore && (
        <Tabs defaultValue="request" className="space-y-5">
          <TabsList className="flex-wrap h-auto" data-testid="npd-tabs">
            <TabsTrigger value="request" data-testid="tab-npd-request">Raise Request</TabsTrigger>
            <TabsTrigger value="return" data-testid="tab-npd-return">Return To Store</TabsTrigger>
            <TabsTrigger value="issue-production" data-testid="tab-npd-issue-production">Issue for Production</TabsTrigger>
            <TabsTrigger value="consume" data-testid="tab-npd-consume">Consume From NPD Godown</TabsTrigger>
            <TabsTrigger value="report" data-testid="tab-npd-report">Activity Report</TabsTrigger>
            <TabsTrigger value="spend" data-testid="tab-npd-spend">Project Spend</TabsTrigger>
          </TabsList>

          <TabsContent value="return" className="space-y-5">
            <NpdReturnToStoreTab sites={sites} />
          </TabsContent>

          <TabsContent value="issue-production" className="space-y-5">
            <NpdIssueForProductionTab sites={sites} />
          </TabsContent>

          <TabsContent value="consume" forceMount className="data-[state=inactive]:hidden">
            <NpdConsumptionTab sites={sites} />
          </TabsContent>

          <TabsContent value="report" forceMount className="data-[state=inactive]:hidden">
            <NpdActivityReportTab sites={sites} />
          </TabsContent>

          <TabsContent value="spend" className="space-y-5">
            <NpdProjectSpendTab sites={sites} />
          </TabsContent>

          <TabsContent value="request" className="space-y-5">
        <div className="bg-white border border-[#D0D5DD] rounded-sm p-4 space-y-4" data-testid="npd-new-request-card">
          <div className="flex items-center gap-2">
            <Flask size={18} weight="fill" className="text-[#0E7C86]" />
            <h2 className="font-heading text-base font-bold uppercase tracking-wide">New NPD Request</h2>
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-3">
            <div className="space-y-1">
              <Label className="text-xs font-bold text-[#344054]">Site *</Label>
              <Select value={siteId} onValueChange={setSiteId}>
                <SelectTrigger className="h-9 bg-white" data-testid="npd-site-select"><SelectValue placeholder="Select site" /></SelectTrigger>
                <SelectContent className="bg-white">
                  {sites.map((s) => <SelectItem key={s} value={s} data-testid={`npd-site-option-${s}`}>{s}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-1">
              <Label className="text-xs font-bold text-[#344054]">Purpose *</Label>
              <Select value={purpose} onValueChange={setPurpose}>
                <SelectTrigger className="h-9 bg-white" data-testid="npd-purpose-select"><SelectValue /></SelectTrigger>
                <SelectContent className="bg-white">
                  <SelectItem value="production" data-testid="npd-purpose-option-production">NPD Project Consumption</SelectItem>
                  <SelectItem value="consumption" data-testid="npd-purpose-option-consumption">Production</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-1">
              <Label className="text-xs font-bold text-[#344054]">Move Into (NPD Godown) *</Label>
              <Select value={targetWarehouse} onValueChange={setTargetWarehouse}>
                <SelectTrigger className="h-9 bg-white" data-testid="npd-target-warehouse-select"><SelectValue placeholder="Select warehouse" /></SelectTrigger>
                <SelectContent className="bg-white max-h-64">
                  {warehouses.map((w) => (
                    <SelectItem key={w.warehouse_id} value={w.warehouse_id} data-testid={`npd-target-warehouse-option-${w.warehouse_id}`}>
                      {w.warehouse_id}{w.warehouse_name ? ` - ${w.warehouse_name}` : ""}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-1">
              <Label className="text-xs font-bold text-[#344054]">Consumed Later Against</Label>
              <div className="h-9 flex items-center px-3 bg-[#F9FAFB] border border-[#EAECF0] rounded-sm text-[12px] font-mono text-[#475467]" data-testid="npd-cost-center-preview">
                {taskId ? `SAP task ${taskId}` : "the SAP task you pick"}
              </div>
            </div>
            <div className="space-y-1">
              <Label className="text-xs font-bold text-[#344054]">SAP Project *</Label>
              <NpdProjectPicker
                value={projectId}
                name={projectName}
                onPick={(p) => {
                  setProjectId(p.project_id); setProjectName(p.name); setTaskId(""); setTaskName("");
                  setNewCustomer(projectCustomerName(p, newCustomer));
                }}
              />
              <p className="text-[10px] text-[#667085]" data-testid="npd-project-hint">
                Live from SAP. Closed projects are hidden unless you tick "include closed".
              </p>
            </div>
            <div className="space-y-1">
              <Label className="text-xs font-bold text-[#344054]">SAP Task *</Label>
              <NpdTaskPicker
                projectId={projectId}
                value={taskId}
                onPick={(t) => { setTaskId(t.task_id); setTaskName(t.name || ""); }}
              />
              <p className="text-[10px] text-[#667085]" data-testid="npd-task-hint">
                The material is consumed against this task in SAP.
              </p>
            </div>
            <div className="space-y-1">
              <Label className="text-xs font-bold text-[#344054]">Customer Name</Label>
              <Input
                value={newCustomer}
                onChange={(e) => setNewCustomer(e.target.value)}
                placeholder="Filled from the project - edit if needed"
                className="h-9"
                data-testid="npd-new-customer-input"
              />
              <p className="text-[11px] text-[#667085]" data-testid="npd-customer-hint">
                Optional. Filled from the project name where it gives the customer away.
              </p>
            </div>
            <div className="space-y-1 sm:col-span-2">
              <NpdNoteField
                value={remark}
                onChange={setRemark}
                projectId={projectId}
                lines={filledLines.map((l) => ({ product_id: l.product_id.trim().toUpperCase(), quantity: Number(l.quantity) }))}
                label="Note *"
                testIdPrefix="npd-remark"
              />
            </div>
          </div>

          <div className="rounded-sm px-3 py-2 text-xs border bg-[#F0FDF9] border-[#A6F4C5] text-[#027A48]" data-testid="npd-purpose-notice">
            The store will move the stock from the raw-material godown (or whichever zone actually holds it) into <strong>{siteId ? `${siteId}/${targetWarehouse}` : "the NPD godown"}</strong>.
            {" "}NPD then consumes it from there against the SAP project task{taskId ? <> <strong>{taskId}</strong></> : null}.
          </div>

          <div className="border border-[#D0D5DD] rounded-sm overflow-x-auto">
            <table className="w-full min-w-[560px] text-[12px] border-collapse" data-testid="npd-items-table">
              <thead>
                <tr>
                  {["Item Code", "Description", "Unit", "Qty", ""].map((h) => (
                    <th key={h} className="bg-[#EAECF0] border border-[#D0D5DD] p-1.5 text-left text-xs font-bold text-[#344054] font-heading uppercase whitespace-nowrap">{h}</th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {lines.map((line, i) => (
                  <ItemRow
                    key={line.key}
                    line={line}
                    index={i}
                    siteId={siteId}
                    canRemove={lines.length > 1}
                    onChange={(next) => setLines((prev) => prev.map((l) => (l.key === line.key ? next : l)))}
                    onRemove={() => setLines((prev) => prev.filter((l) => l.key !== line.key))}
                  />
                ))}
              </tbody>
            </table>
          </div>

          <div className="flex flex-wrap items-center gap-2">
            <Button type="button" variant="outline" className="bg-white" onClick={() => setLines((prev) => [...prev, emptyLine()])} data-testid="npd-add-item-button">
              <Plus size={14} className="mr-1.5" /> Add Item
            </Button>
            <Button type="button" disabled={submitting || !!missingField} onClick={submit} data-testid="npd-submit-request-button">
              {submitting ? "Sending..." : "Send to Store"}
            </Button>
            <span className="text-xs text-[#667085]" data-testid="npd-submit-hint">{missingField || `${filledLines.length} item(s) ready`}</span>
          </div>
        </div>

        <div className="bg-white border border-[#D0D5DD] rounded-sm" data-testid="npd-my-requests-card">
          <div className="flex items-center justify-between gap-2 px-4 py-3 border-b border-[#EAECF0]">
            <h2 className="font-heading text-base font-bold uppercase tracking-wide">My NPD Requests</h2>
            <Button variant="outline" size="sm" className="h-8 bg-white" onClick={loadRequests} data-testid="npd-refresh-requests-button">
              <ArrowClockwise size={13} className={`mr-1.5 ${loadingRequests ? "animate-spin" : ""}`} /> Refresh
            </Button>
          </div>
          <div className="overflow-auto">
            <table className="w-full text-[12px] border-collapse" data-testid="npd-requests-table">
              <thead>
                <tr>
                  {["Request ID", "Raised", "Purpose", "Site", "Items", "Issue ID", "Status", ""].map((h) => (
                    <th key={h} className="bg-[#EAECF0] border border-[#D0D5DD] p-1.5 text-left text-xs font-bold text-[#344054] font-heading uppercase whitespace-nowrap">{h}</th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {requests.map((r, i) => (
                  <tr key={r._id} className={i % 2 === 0 ? "bg-white" : "bg-[#F9FAFB]"} data-testid={`npd-request-row-${i}`}>
                    <td className="border border-[#D0D5DD] px-2 py-1.5 font-mono font-bold text-[#175CD3]" data-testid={`npd-request-id-${i}`}>{r._id}</td>
                    <td className="border border-[#D0D5DD] px-2 py-1.5">{new Date(r.created_at).toLocaleString("en-IN")}</td>
                    <td className="border border-[#D0D5DD] px-2 py-1.5">{PURPOSE_LABEL[r.purpose] || r.purpose}</td>
                    <td className="border border-[#D0D5DD] px-2 py-1.5">{r.site_id}</td>
                    <td className="border border-[#D0D5DD] px-2 py-1.5">
                      {(r.components || []).map((c) => (
                        <div key={c.product_id} className="whitespace-nowrap">
                          <span className="font-mono">{c.product_id}</span>
                          <span className="text-[#667085]"> &middot; {formatQty(c.required_qty)} {formatUnit(c.unit_of_measure)}</span>
                          <RoundedUpFlag component={c} />
                          {c.issued_qty != null && <span className="text-[#027A48]"> &rarr; issued {formatQty(c.issued_qty)}</span>}
                        </div>
                      ))}
                    </td>
                    <td className="border border-[#D0D5DD] px-2 py-1.5 font-mono text-[#0E7C86]">{r.issue_id || "\u2014"}</td>
                    <td className="border border-[#D0D5DD] px-2 py-1.5">
                      <Badge className={`${STATUS_BADGE[r.status]?.tone || ""} border`} data-testid={`npd-request-status-${i}`}>{STATUS_BADGE[r.status]?.label || r.status}</Badge>
                      {r.status === "awaiting_receipt" && (
                        <p className="text-[10px] text-[#B93815] mt-1 max-w-[180px]" data-testid={`npd-receipt-hint-${r._id}`}>
                          Count what you received - SAP moves the stock only once you confirm.
                        </p>
                      )}
                      {r.resolution === "cancelled_by_requester" && (
                        <p className="text-[10px] text-[#B42318] mt-1 max-w-[180px]" data-testid={`npd-cancel-remark-${r._id}`}>
                          Variance: {r.requester_cancel_remark}
                        </p>
                      )}
                    </td>
                    <td className="border border-[#D0D5DD] px-2 py-1.5">
                      <div className="flex flex-wrap gap-1.5">
                        {r.status === "awaiting_receipt" && isMine(r) && (
                          <>
                            <Button
                              size="sm" className="h-7"
                              disabled={receiptBusyId === r._id}
                              onClick={() => confirmReceipt(r)}
                              data-testid={`npd-confirm-receipt-${r._id}`}
                            >
                              <CheckCircle size={13} className="mr-1" />
                              {receiptBusyId === r._id ? "Posting..." : "Confirm Receipt"}
                            </Button>
                            <Button
                              size="sm" variant="outline" className="h-7 bg-white text-[#B42318] border-[#FECDCA]"
                              disabled={receiptBusyId === r._id}
                              onClick={() => { setCancelTarget(r); setCancelRemark(""); }}
                              data-testid={`npd-cancel-request-${r._id}`}
                            >
                              <XCircle size={13} className="mr-1" /> Cancel
                            </Button>
                          </>
                        )}
                        <Button variant="outline" size="sm" className="h-7 bg-white" onClick={() => setPrintTarget(r)} data-testid={`npd-print-request-${i}`}>
                          <Printer size={13} className="mr-1" /> Print
                        </Button>
                      </div>
                    </td>
                  </tr>
                ))}
                {!loadingRequests && requests.length === 0 && (
                  <tr><td colSpan={8} className="text-center py-8 text-[#98A2B3] border border-[#D0D5DD]" data-testid="npd-requests-empty-state">
                    No NPD requests raised yet.
                  </td></tr>
                )}
              </tbody>
            </table>
          </div>
        </div>

        <p className="text-[11px] text-[#667085] flex items-center gap-1.5">
          <MagnifyingGlass size={12} /> Once sent, the request appears in the store team's Store Goods Issue queue. They record what
          they issued and hand it over - then it comes back here as <strong>Confirm Your Receipt</strong>. SAP only moves the stock when
          you confirm; if the physical quantity does not match, cancel it with a remark instead and the store is notified.
        </p>

        <Dialog open={!!cancelTarget} onOpenChange={(v) => { if (!v) { setCancelTarget(null); setCancelRemark(""); } }}>
          <DialogContent className="sm:max-w-[520px]" data-testid="npd-cancel-dialog">
            <DialogHeader>
              <DialogTitle className="font-heading text-base">Cancel {cancelTarget?._id}</DialogTitle>
            </DialogHeader>
            <div className="space-y-3">
              <p className="text-xs text-[#475467]">
                Use this when what you physically received does not match what the store issued. Nothing has been posted to SAP, so the
                stock stays with the store - this closes the request for good and emails them your remark. Raise a fresh request once
                the count is sorted out.
              </p>
              {(cancelTarget?.components || []).map((c) => (
                <p key={c.product_id} className="text-[11px] text-[#667085]">
                  <span className="font-mono">{c.product_id}</span> - store issued {formatQty(c.issued_qty)} {formatUnit(c.unit_of_measure)}
                </p>
              ))}
              <div className="space-y-1.5">
                <Label className="text-xs font-bold text-[#344054]">What did not match?</Label>
                <Textarea
                  value={cancelRemark}
                  onChange={(e) => setCancelRemark(e.target.value)}
                  rows={3}
                  placeholder="e.g. Store issued 10 EA, only 8 EA physically received"
                  data-testid="npd-cancel-remark-input"
                />
              </div>
            </div>
            <DialogFooter>
              <Button variant="outline" onClick={() => setCancelTarget(null)} data-testid="npd-cancel-dialog-close">Keep Waiting</Button>
              <Button
                className="bg-[#B42318] hover:bg-[#912018]"
                disabled={!!receiptBusyId}
                onClick={cancelWithRemark}
                data-testid="npd-cancel-confirm-button"
              >
                {receiptBusyId ? "Cancelling..." : "Cancel This Request"}
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>

          </TabsContent>
        </Tabs>
        )}
      </main>
    </div>
  );
}
